import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import api from "../../../../api";
import { QUERIES_KEYS } from "../../../../constants/queries-keys";
import useAttachmentsAPI from "../../../../hooks/useAttachmentsAPI";
import useFormatMessage from "../../../../hooks/useFormatMessage";
import useQueryData from "../../../../hooks/useQueryData";
import { Entity } from "../../../../types/utilities";
import { isEmail } from "../../../../utils/stringUtils";
import { mapToAttachmentSignature, mapToCreateOrUpdateAttachmentSignature } from "../../AttachmentsSection.helpers";
import { AttachmentSignature } from "../../AttachmentsSection.types";

import { isNameValid, isNationalIdValid } from "./AttachmentSignaturesModal.helpers";
import useEditableRows, { Row } from "./useEditableRows";

export type AttachmentSignatureRow = Row<AttachmentSignature>;

export type SuggestionsProps = {
  row: AttachmentSignatureRow;
  personId: string;
  personName: string;
  email?: string;
  roleId?: string;
  roleType?: api.RoleType;
};

const hasUnsentEmail = (row: AttachmentSignatureRow) =>
  row.entity.send_email && (row.isNew || row.originalEntity?.email !== row.entity.email);

const hasUnsavedLink = (row: AttachmentSignatureRow) => !row.entity.send_email && row.isNew;

const useAttachmentSignaturesModal = (entity: Entity, attachment: api.AttachmentDetails) => {
  const queryClient = useQueryClient();
  const f = useFormatMessage();

  const { entityId, getAttachmentSignatures, updateAttachmentSignatures } = useAttachmentsAPI(entity, attachment);

  const attachmentsSignaturesQueryKey = [QUERIES_KEYS.ATTACHMENT_SIGNATURES, entityId, attachment.id];

  const { setQueryData } = useQueryData<api.AttachmentSignaturesResponse>(attachmentsSignaturesQueryKey);

  const {
    isLoading,
    isError: isLoadingError,
    error: loadingError,
    data
  } = useQuery(attachmentsSignaturesQueryKey, () => getAttachmentSignatures(entityId, attachment.id));

  const saveAttachmentSignaturesMutation = useMutation<
    api.AttachmentSignaturesResponse,
    unknown,
    { updateAttachmentSignaturesRequest: api.UpdateAttachmentSignaturesRequest }
  >(
    ({ updateAttachmentSignaturesRequest }) =>
      updateAttachmentSignatures(entityId, attachment.id, updateAttachmentSignaturesRequest),
    {
      onSuccess: (response) => {
        setQueryData(response);
        queryClient.invalidateQueries([QUERIES_KEYS.ATTACHMENTS, entityId]);
      }
    }
  );

  const attachedSignatures = useMemo(() => (data?.signatures || []).map(mapToAttachmentSignature), [data]);

  const { rows, updateProperty, saveRow, ...rowsMethods } = useEditableRows<AttachmentSignature>({
    entities: attachedSignatures,
    createEmptyEntity: () => ({
      id: String("new-row-" + Math.round(Math.random() * 1_000_000)),
      fullName: "",
      send_email: true,
      use_verification: false,
      signing_page_url: "",
      pin: "",
      status: "PENDING"
    }),
    isValidEntity: (entity, rows) => {
      const validationErrors = new Map<keyof AttachmentSignature, string>();

      // validate name
      const nameValidationError = isNameValid(entity.fullName);

      if (nameValidationError) {
        validationErrors.set("fullName", f(nameValidationError));
      } else {
        const nonEmptyNames = rows.map((row) => row.entity.fullName.trim().toLowerCase()).filter(Boolean);
        const allNames = new Set(nonEmptyNames);
        const isUniqueName = allNames.size === nonEmptyNames.length;

        if (!isUniqueName) {
          validationErrors.set("fullName", f("common.errors.invalid.name.exists"));
        }
      }

      // validate email
      const isEmailValid = !entity.send_email || isEmail(entity.email);

      if (!isEmailValid) {
        validationErrors.set("email", f("email.invalid.address.error"));
      } else {
        const allEmails = rows.map((row) => row.entity.email?.toLowerCase());
        const isUniqueEmail =
          !entity.send_email ||
          allEmails.indexOf(entity.email?.toLowerCase()) === allEmails.lastIndexOf(entity.email?.toLowerCase());

        if (!isUniqueEmail) {
          validationErrors.set("email", f("common.errors.email.not-unique"));
        }
      }

      // validate national id
      const isValidNationalId = !entity.use_verification || isNationalIdValid(entity.national_id);
      if (!isValidNationalId) {
        validationErrors.set("national_id", f("common.errors.invalid.person.national-id"));
      } else {
        const allNationalIds = rows.map((row) => row.entity.national_id?.toLowerCase());
        const isUniqueNationalId =
          allNationalIds.indexOf(entity.national_id?.toLowerCase()) ===
          allNationalIds.lastIndexOf(entity.national_id?.toLowerCase());

        if (entity.use_verification && !isUniqueNationalId) {
          validationErrors.set("national_id", f("common.errors.invalid.national-id.exists"));
        }
      }

      return validationErrors;
    },
    isEqualEntity: (firstEntity, secondEntity) => {
      return (
        firstEntity.fullName === secondEntity.fullName &&
        firstEntity.send_email === secondEntity.send_email &&
        firstEntity.email === secondEntity.email &&
        firstEntity.role?.id === secondEntity.role?.id &&
        firstEntity.use_verification === secondEntity.use_verification &&
        firstEntity.national_id === secondEntity.national_id
      );
    }
  });

  const saveChanges = () => {
    const currentAttachmentSignatures = rows
      // remove local id from new rows
      .map((row) => (row.isNew ? { ...row.entity, id: undefined } : row.entity))
      .map(mapToCreateOrUpdateAttachmentSignature);

    const previousAttachmentSignaturesCount = data?.signatures?.length || 0;
    // tells the server to delete the signicat document associated with signatures for this attachment
    const deleteSignaturesDocument = previousAttachmentSignaturesCount > 0 && currentAttachmentSignatures.length === 0;

    return saveAttachmentSignaturesMutation.mutateAsync({
      updateAttachmentSignaturesRequest: {
        signatures: currentAttachmentSignatures,
        delete: deleteSignaturesDocument
      }
    });
  };

  const selectSuggestion = ({ row, personId, personName, email, roleId, roleType }: SuggestionsProps) => {
    updateProperty(row, "signer_id", personId);
    updateProperty(row, "fullName", personName);
    updateProperty(row, "email", email);
    updateProperty(row, "role", roleId ? { id: roleId, type: roleType!, status: "ACTIVE" } : undefined);
  };

  const clearSuggestion = (row: AttachmentSignatureRow) => {
    updateProperty(row, "signer_id", undefined);
    updateProperty(row, "fullName", "");
    updateProperty(row, "email", "");
    updateProperty(row, "role", undefined);
  };

  const handleSaveRow = (row: AttachmentSignatureRow) => {
    if (!row.entity.send_email) {
      row.entity.email = undefined;
    }
    saveRow(row);
  };

  const hasInvalidRows = rows.find((row) => row.isCreating || row.validationErrors.size !== 0) !== undefined;
  const hasUnsavedChanges =
    !hasInvalidRows && (rows.find((row) => row.isModified) !== undefined || rows.length !== attachedSignatures.length);
  const isEmailOnly = rows.find((row) => !row.entity.send_email) === undefined;
  const isLinkOnly = rows.find((row) => row.entity.send_email) === undefined;
  const hasUnsentEmails = rows.filter(hasUnsentEmail).length > 0;
  const hasUnsavedLinks = rows.filter(hasUnsavedLink).length > 0;

  return {
    isLoading,
    isError: isLoadingError,
    error: loadingError || saveAttachmentSignaturesMutation.error,
    isInitialEmpty: !isLoading && data?.signatures.length === 0,
    isSaving: saveAttachmentSignaturesMutation.isLoading,
    hasInvalidRows,
    hasUnsavedChanges,
    isEmailOnly,
    isLinkOnly,
    hasUnsentEmails,
    hasUnsavedLinks,
    rows,
    updateProperty,
    saveRow: handleSaveRow,
    saveChanges,
    selectSuggestion,
    clearSuggestion,
    ...rowsMethods
  };
};

export default useAttachmentSignaturesModal;
